'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { useForm } from 'react-hook-form'
import { useTranslations } from 'next-intl'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { lookupBooking } from '@/services/booking-actions'

function createLookupSchema(t: (key: string) => string) {
  return z.object({
    confirmationCode: z
      .string()
      .min(1, t('codeRequired'))
      .regex(/^[A-Z]{2,4}\d+$/, t('invalidCode')),
    phone: z.string().min(4, t('phoneRequired')),
  })
}

type LookupFormValues = z.infer<ReturnType<typeof createLookupSchema>>

/**
 * Client Component: Lookup Form
 *
 * Form validation on client, submission via server action.
 * Uses phone number instead of last name for guest verification.
 */
export function LookupForm() {
  const router = useRouter()
  const t = useTranslations('booking.confirmation.lookup')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const lookupSchema = createLookupSchema(t)

  const form = useForm<LookupFormValues>({
    resolver: zodResolver(lookupSchema),
    defaultValues: {
      confirmationCode: '',
      phone: '',
    },
  })

  const onSubmit = async (data: LookupFormValues) => {
    setIsLoading(true)
    setError(null)

    try {
      const formData = new FormData()
      formData.append('confirmationCode', data.confirmationCode)
      formData.append('phone', data.phone)

      const result = await lookupBooking(formData)

      if (!result.success) {
        setError(result.error || 'Lookup failed')
        return
      }

      if (result.redirectUrl) {
        router.push(result.redirectUrl)
      }
    } catch (err) {
      console.error('Lookup error:', err)
      setError(err instanceof Error ? err.message : 'Lookup failed')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="confirmationCode"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('confirmationCode')}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t('codePlaceholder')}
                  {...field}
                  onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                  className="uppercase"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('phoneNumber')}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t('phonePlaceholder')}
                  type="tel"
                  inputMode="tel"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? t('searchingLabel') : t('findReservation')}
        </Button>
      </form>
    </Form>
  )
}
